/**
 * Filter out the keys with values that are undefined or null from the input object
 * @typedef {Object} filterUndefinedOrNull
 * @property {string} obj - 要過濾的的物件
 */
export const filterUndefinedOrNull = obj => {
    if (!obj || typeof obj !== 'object') return {};
    if (Object.keys(obj).length === 0) return obj;

    for (const key in obj) {
        if (obj[key] === undefined || obj[key] === null) {
            delete obj[key];
        }
    }
    return obj;
};

/**
 * @description Masks a given string or value by replacing the middle portion with asterisks (`*`),
 *              while keeping a specified number of characters visible at the start and end.
 * @param {string} value - The string to be masked.
 * @param {number} [visibleCharsStart=0] - The number of characters to keep visible at the start of the string.
 * @param {number} [visibleCharsEnd=0] - The number of characters to keep visible at the end of the string.
 * @returns {string} - The masked string with asterisks in the middle and visible characters at the start and end.
 *
 * @example
 * // Example 1: Masking an email address
 * const maskedEmail = maskValue('s110319022@stu.ntue.edu.tw', 2, 10);
 * // maskedEmail -> "s1********@stu.ntue.edu.tw"
 *
 * @example
 * // Example 2: Masking an access token
 * const maskedToken = maskValue('EAADTmsjG8VMBO1UCaT7ucEqixaJgiExvXyGwaUt', 4, 4);
 * // maskedToken -> "EAAD************aUt"
 */
export function maskValue(value, visibleCharsStart = 0, visibleCharsEnd = 0) {
    const totalLength = value.length;
    const maskedLength =
        totalLength > visibleCharsStart + visibleCharsEnd
            ? totalLength - visibleCharsStart - visibleCharsEnd
            : totalLength;
    const maskedPortion = '*'.repeat(maskedLength);
    if (maskedLength === value.length) return maskedPortion;
    return (
        value.substring(0, visibleCharsStart) +
        maskedPortion +
        value.substring(totalLength - visibleCharsEnd)
    );
}

/**
 * @description Recursive function to traverse and mask the object.
 *              It masks specific keys based on the provided keysToMask configuration.
 * @param {Object} obj - The object to traverse and mask.
 * @param {Object} keysToMask - An object defining which keys to mask and how many characters to show.
 * @param {Object} keysToMask.key - For each key to be masked, specify the number of visible characters at the start and end.
 * @param {number} keysToMask.key.visibleStart - The number of characters to show at the start of the value.
 * @param {number} keysToMask.key.visibleEnd - The number of characters to show at the end of the value.
 * @returns {Object} - A new object with masked values.
 *
 * @example
 * const keysToMask = {
 *   "id": { visibleStart: 4, visibleEnd: 4 },
 *   "app_id": { visibleStart: 4, visibleEnd: 4 },
 *   "member_uuid": { visibleStart: 4, visibleEnd: 4 },
 *   "email": { visibleStart: 2, visibleEnd: 10 },
 *   "fullname": { visibleStart: 1, visibleEnd: 0 },
 *   "gender": { visibleStart: 0, visibleEnd: 0 },  // Completely mask gender
 *   "access_token": { visibleStart: 4, visibleEnd: 4 }
 * };
 *
 * recursiveMask(userLoginInfo, keysToMask);
 */
export async function recursiveMask(obj, keysToMask) {
    const _obj = { ...obj };

    for (let key in _obj) {
        if (typeof _obj[key] === 'object' && _obj[key] !== null) {
            // If the value is an object, call the function recursively
            _obj[key] = await recursiveMask(_obj[key], keysToMask);
        } else {
            // Apply masking for keys that are in the keysToMask argument
            if (keysToMask[key]) {
                if (typeof _obj[key] === 'string' || typeof _obj[key] === 'number') {
                    const valueStr = _obj[key].toString();
                    const { visibleStart, visibleEnd } = keysToMask[key];
                    _obj[key] = maskValue(valueStr, visibleStart, visibleEnd);
                }
            }
        }
    }
    return _obj;
}
/**
 * 替換文本中的變量
 * @param {string} text - 要處理的文本
 * @param {Object|string} [data] - 要替換成的數據，可以是字符串或對象。若為對象，則鍵名對應變量名
 * @returns {string} 處理後的文本
 */
export const replaceTextVariable = (text, data) => {
    // 從文本中提取所有變量名
    const extractVariables = txt => {
        const atRegex = /@([a-zA-Z0-9_]+)/g;
        const systemRegex = /{{system:([a-zA-Z0-9_]+)}}/g;

        const atMatches = [...txt.matchAll(atRegex)].map(match => match[1]);
        const systemMatches = [...txt.matchAll(systemRegex)].map(match => match[1]);

        return [...new Set([...atMatches, ...systemMatches])];
    };

    const variables = extractVariables(text);

    if (variables.length === 0) {
        return text;
    }

    let result = text;

    // 如果提供了數據
    if (data !== undefined) {
        // 處理對象類型的數據
        if (typeof data === 'object' && data !== null) {
            // 檢查對象是否為空
            if (Object.keys(data).length === 0) {
                return text; // 如果是空對象，直接返回原文本
            }

            for (const variable of variables) {
                // 只有當對象中存在對應的鍵時才進行替換
                if (data[variable] !== undefined) {
                    const value = data[variable] === null ? '' : data[variable];
                    result = result
                        .replace(new RegExp(`@${variable}`, 'g'), value)
                        .replace(new RegExp(`{{system:${variable}}}`, 'g'), value);
                }
            }
        }
        // 處理字符串類型的數據 (所有變量替換為相同的值)
        else {
            const value = data === null ? '' : data;
            for (const variable of variables) {
                result = result
                    .replace(new RegExp(`@${variable}`, 'g'), value)
                    .replace(new RegExp(`{{system:${variable}}}`, 'g'), value);
            }
        }
        return result;
    }

    // 如果沒有提供數據，則進行格式轉換
    for (const variable of variables) {
        if (result.includes(`@${variable}`)) {
            result = result.replace(new RegExp(`@${variable}`, 'g'), `{{system:${variable}}}`);
        } else if (result.includes(`{{system:${variable}}}`)) {
            result = result.replace(new RegExp(`{{system:${variable}}}`, 'g'), `@${variable}`);
        }
    }

    return result;
};
